/**
 * Inbox - the operator's unified, contact-threaded conversation inbox
 * (SPEC E1c - unified-inbox, R4 + inbox-review amendments).
 *
 * Three panes:
 *   LEFT   - contact list (conversation-gated), with client-side SEARCH +
 *            a CHANNEL FILTER dropdown, last-message preview, channel icons,
 *            unread badge, relative time.
 *   CENTER - selected contact's threads (one tab per channel) + message history
 *            + a compose bar (Send / Note toggle) + inline draft-assist
 *            ("Suggest a reply" / "Use draft") + the Messenger 24h
 *            WINDOW_EXPIRED state + the pending-AI-draft banner → /ai-drafts.
 *   RIGHT  - contact-context panel (name/company/email/phone, open
 *            opportunities, last activity, link to the contact).
 *
 * Poll-on-focus refresh (no websockets) + a manual Refresh button.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  RefreshCw,
  Search,
  Send,
  Bot,
  Sparkles,
  StickyNote,
  Building2,
  Mail,
  Phone,
  ExternalLink,
  AlertCircle,
  ArrowLeft,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useInbox,
  useContactConversations,
  useConversation,
  useContact,
  useContactOpportunities,
  usePendingDraft,
  useSendMessage,
  useAddNote,
} from '@/lib/api/hooks/useInbox';
import { SENDABLE_CHANNELS } from '@/types/inbox';
import type {
  InboxContact,
  InboxMessage,
  ConversationDetail,
} from '@/types/inbox';

// ─── helpers ──────────────────────────────────────────────────────────────────

function relativeTime(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function channelLabel(channel?: string | null): string {
  if (!channel) return 'Channel';
  const map: Record<string, string> = {
    messenger: 'Messenger',
    sms: 'SMS',
    email: 'Email',
    whatsapp: 'WhatsApp',
    twitter_dm: 'Twitter DM',
    twitter_reply: 'Twitter',
    twitter_mention: 'Twitter',
    voice: 'Calls',
  };
  return map[channel] ?? channel;
}

/** Is the Messenger 24h window still open for this conversation? */
function isMessengerWindowOpen(conv?: ConversationDetail | null): boolean {
  if (!conv || conv.channel !== 'messenger') return true; // non-messenger: n/a
  const last = conv.last_inbound_at ? new Date(conv.last_inbound_at).getTime() : NaN;
  if (Number.isNaN(last)) return false;
  return Date.now() - last <= 24 * 60 * 60 * 1000;
}

// ─── left pane: contact row ─────────────────────────────────────────────────────

function ContactRow({
  contact,
  active,
  onSelect,
}: {
  contact: InboxContact;
  active: boolean;
  onSelect: () => void;
}) {
  const top = contact.channels[0];
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`inbox-contact-${contact.contact_id}`}
      className={cn(
        'w-full text-left px-3 py-3 border-b transition-colors',
        active ? 'bg-accent' : 'hover:bg-accent/50'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium truncate">{contact.display_name}</span>
        <span className="text-xs text-muted-foreground shrink-0">
          {relativeTime(contact.last_active_at)}
        </span>
      </div>
      {contact.company_name && (
        <div className="text-xs text-muted-foreground truncate">{contact.company_name}</div>
      )}
      <div className="flex items-center gap-2 mt-1">
        <div className="flex gap-1">
          {contact.channels.map((c) => (
            <Badge key={c.conversation_id} variant="outline" className="text-[10px] px-1 py-0">
              {channelLabel(c.channel)}
            </Badge>
          ))}
        </div>
        {contact.unread_total > 0 && (
          <Badge variant="default" className="text-[10px] ml-auto" data-testid="unread-badge">
            {contact.unread_total}
          </Badge>
        )}
      </div>
      {top?.last_message_preview && (
        <p className="text-xs text-muted-foreground italic truncate mt-1">
          {top.last_message_preview}
        </p>
      )}
    </button>
  );
}

// ─── center pane: message bubble ────────────────────────────────────────────────

function MessageBubble({ msg }: { msg: InboxMessage }) {
  if (msg.direction === 'note') {
    return (
      <div className="mx-auto my-1 max-w-[80%] rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        <div className="flex items-center gap-1 font-medium">
          <StickyNote className="h-3 w-3" />
          Internal note · {msg.sender_display_name || 'Operator'}
        </div>
        <p className="whitespace-pre-wrap mt-0.5">{msg.content}</p>
      </div>
    );
  }
  const inbound = msg.direction === 'inbound';
  return (
    <div className={cn('flex', inbound ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'max-w-[75%] rounded-lg px-3 py-2 text-sm',
          inbound ? 'bg-muted text-foreground' : 'bg-primary text-primary-foreground'
        )}
      >
        <p className="whitespace-pre-wrap">{msg.content}</p>
        <div
          className={cn(
            'text-[10px] mt-1',
            inbound ? 'text-muted-foreground' : 'text-primary-foreground/70'
          )}
        >
          {inbound ? msg.sender_display_name || msg.sender_handle || 'Them' : 'You'} ·{' '}
          {relativeTime(msg.received_at || msg.created_at)}
        </div>
      </div>
    </div>
  );
}

// ─── right pane: contact-context panel ──────────────────────────────────────────

function ContactContextPanel({ contactId }: { contactId: string }) {
  const { data: contact } = useContact(contactId);
  const { data: opportunities } = useContactOpportunities(contactId);
  const email =
    contact?.email ||
    contact?.channels?.find((c) => c.channel_type === 'email')?.channel_value;
  const phone =
    contact?.phone ||
    contact?.channels?.find((c) => c.channel_type === 'phone' || c.channel_type === 'sms')
      ?.channel_value;

  return (
    <aside className="w-full lg:w-72 border-l bg-card flex flex-col overflow-y-auto" data-testid="context-panel">
      <div className="p-4 border-b">
        <h3 className="font-semibold truncate">{contact?.display_name || 'Contact'}</h3>
        {contact?.job_title && (
          <p className="text-xs text-muted-foreground">{contact.job_title}</p>
        )}
        {contact?.lifecycle_status && (
          <Badge variant="secondary" className="mt-1 text-[10px]">
            {contact.lifecycle_status}
          </Badge>
        )}
      </div>

      <div className="p-4 space-y-2 border-b text-sm">
        {contact?.company_name && (
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{contact.company_name}</span>
          </div>
        )}
        {email && (
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{email}</span>
          </div>
        )}
        {phone && (
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{phone}</span>
          </div>
        )}
      </div>

      <div className="p-4 border-b">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          Open opportunities
        </p>
        {opportunities && opportunities.length > 0 ? (
          <ul className="space-y-2">
            {opportunities.map((opp) => (
              <li key={opp.id} className="text-sm border rounded-md px-2 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{opp.name || opp.stage_key}</span>
                  {opp.value != null && (
                    <span className="text-xs text-muted-foreground shrink-0">
                      {opp.currency || ''} {opp.value}
                    </span>
                  )}
                </div>
                <Badge variant="outline" className="text-[10px] mt-1">
                  {opp.stage_key}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground italic">No open opportunities.</p>
        )}
      </div>

      <div className="p-4 mt-auto">
        <Link
          to="/pipeline"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          View in pipeline <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </aside>
  );
}

// ─── center pane: thread + compose ──────────────────────────────────────────────

function ThreadPane({ conversationId }: { conversationId: string }) {
  const { data: conv, isLoading } = useConversation(conversationId);
  const { data: pendingDraft } = usePendingDraft(conversationId);
  const send = useSendMessage();
  const addNote = useAddNote();

  const [draft, setDraft] = useState('');
  const [noteMode, setNoteMode] = useState(false);

  const channel = conv?.channel ?? '';
  const sendable = SENDABLE_CHANNELS.has(channel);
  const windowOpen = isMessengerWindowOpen(conv);
  const standby = conv?.standby_state === true;
  // The compose bar can SEND when the channel is sendable, the messenger window
  // is open, and not in standby. Notes are always allowed (operator-only).
  const canSend = noteMode || (sendable && windowOpen && !standby);

  function disabledReason(): string {
    if (noteMode) return '';
    if (!sendable) return 'Send not available for this channel';
    if (!windowOpen) return 'Messenger window closed - reply from the contact required';
    if (standby) return 'A human or another app holds this thread';
    return '';
  }

  function handleSend() {
    const content = draft.trim();
    if (!content) return;
    if (noteMode) {
      addNote.mutate(
        { conversationId, content },
        {
          onSuccess: () => {
            setDraft('');
            toast.success('Note added');
          },
          onError: (err) => toast.error(`Failed to add note: ${err.message}`),
        }
      );
      return;
    }
    send.mutate(
      { conversationId, content },
      {
        onSuccess: () => {
          setDraft('');
          toast.success('Message sent');
        },
        onError: (err) => toast.error(`Failed to send: ${err.message}`),
      }
    );
  }

  /** Inline draft-assist: pull the pending AI draft into the compose textarea so
   * the operator edits + sends (still through the human-approve gate - Send is the
   * approve action; no auto-send). */
  function useDraft() {
    if (pendingDraft?.draft_content) {
      setNoteMode(false);
      setDraft(pendingDraft.draft_content);
    }
  }

  const inFlight = send.isPending || addNote.isPending;
  const messages = conv?.messages ?? [];

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Pending-draft banner - links to /ai-drafts (no approval UI duplicated). */}
      {pendingDraft && (
        <div
          className="flex items-center justify-between gap-2 border-b bg-blue-50 px-4 py-2 text-sm"
          data-testid="pending-draft-banner"
        >
          <span className="flex items-center gap-2 text-blue-900">
            <Bot className="h-4 w-4" /> AI draft pending
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={useDraft} data-testid="use-draft-btn">
              <Sparkles className="h-3 w-3 mr-1" /> Use draft
            </Button>
            <Link to="/ai-drafts" className="text-blue-700 hover:underline text-xs">
              Review in AI Drafts →
            </Link>
          </div>
        </div>
      )}

      {/* Message list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="thread-messages">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-2/3" />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground mt-8">No messages yet.</p>
        ) : (
          messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)
        )}
      </div>

      {/* Compose bar */}
      <div className="border-t p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={noteMode ? 'default' : 'outline'}
            onClick={() => setNoteMode((m) => !m)}
            data-testid="note-toggle"
          >
            <StickyNote className="h-3 w-3 mr-1" />
            {noteMode ? 'Note mode' : 'Note'}
          </Button>
          {pendingDraft && !noteMode && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={useDraft}
              data-testid="suggest-reply-btn"
            >
              <Sparkles className="h-3 w-3 mr-1" /> Suggest a reply
            </Button>
          )}
          {!canSend && !noteMode && (
            <span className="text-xs text-destructive" data-testid="compose-disabled-reason">
              {disabledReason()}
            </span>
          )}
        </div>
        <div className="flex items-end gap-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={noteMode ? 'Add an internal note (not sent)…' : 'Type a reply…'}
            rows={2}
            disabled={!canSend}
            data-testid="compose-textarea"
            className={cn('text-sm resize-none', noteMode && 'bg-amber-50')}
          />
          <Button
            onClick={handleSend}
            disabled={!canSend || inFlight || !draft.trim()}
            data-testid="compose-send"
          >
            {noteMode ? (
              <>
                <StickyNote className="h-4 w-4 mr-1" /> {inFlight ? 'Saving…' : 'Save note'}
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-1" /> {inFlight ? 'Sending…' : 'Send'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── center pane: thread switcher (one tab per channel) ─────────────────────────

function ContactThreads({ contactId }: { contactId: string }) {
  const { data, isLoading } = useContactConversations(contactId);
  const conversations = data?.conversations ?? [];
  const [activeConvId, setActiveConvId] = useState<string | null>(null);

  const activeId = activeConvId ?? conversations[0]?.id ?? null;

  if (isLoading) {
    return (
      <div className="flex-1 p-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
        No conversations for this contact yet.
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-w-0">
      <div className="flex gap-1 border-b px-3 py-2" data-testid="channel-tabs">
        {conversations.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setActiveConvId(c.id)}
            data-testid={`channel-tab-${c.channel}`}
            className={cn(
              'flex items-center gap-1 rounded-md px-3 py-1 text-sm transition-colors',
              activeId === c.id
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            )}
          >
            {channelLabel(c.channel)}
            {c.unread_count > 0 && (
              <Badge variant="secondary" className="text-[10px] px-1 py-0">
                {c.unread_count}
              </Badge>
            )}
          </button>
        ))}
      </div>
      {activeId && <ThreadPane key={activeId} conversationId={activeId} />}
    </div>
  );
}

// ─── page ───────────────────────────────────────────────────────────────────────

const CHANNEL_FILTERS = ['all', 'messenger', 'sms', 'email', 'whatsapp', 'voice'] as const;

export function InboxPage() {
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  // SPEC PX-6 R2 (Unit M) - mobile context drawer toggle. Desktop shows the
  // context panel inline (lg:flex); on a phone it is a toggled drawer.
  const [showContext, setShowContext] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useInbox(
    channelFilter !== 'all' ? { channel: channelFilter } : {}
  );

  const contacts = data?.contacts ?? [];

  // Client-side SEARCH filter (left-pane amendment): match display_name /
  // company_name / last message preview.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      const hay = [
        c.display_name,
        c.company_name || '',
        c.channels[0]?.last_message_preview || '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [contacts, search]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-6 w-6 text-muted-foreground" />
          <h1 className="text-2xl font-heading font-bold tracking-tight">Inbox</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn('h-4 w-4 mr-2', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: contact list + search + channel filter.
            Mobile (Unit M): full-width when no contact is selected, hidden once a
            thread is open (single-pane). Desktop: fixed-width left rail always shown. */}
        <div
          data-testid="inbox-list-pane"
          className={cn(
            'flex flex-col border-r w-full md:w-80 md:min-w-[280px] md:max-w-[360px] md:shrink-0',
            selectedContact ? 'hidden md:flex' : 'flex'
          )}
        >
          <div className="p-3 space-y-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search contacts…"
                className="pl-8 text-sm"
                data-testid="inbox-search"
              />
            </div>
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger className="text-sm" data-testid="channel-filter">
                <SelectValue placeholder="All channels" />
              </SelectTrigger>
              <SelectContent>
                {CHANNEL_FILTERS.map((ch) => (
                  <SelectItem key={ch} value={ch}>
                    {ch === 'all' ? 'All channels' : channelLabel(ch)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 overflow-y-auto" data-testid="contact-list">
            {isLoading ? (
              <div className="p-3 space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : error ? (
              <div className="p-6 text-center">
                <AlertCircle className="h-8 w-8 mx-auto text-destructive mb-2" />
                <p className="text-sm text-muted-foreground">{error.message}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center" data-testid="inbox-empty">
                <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <h3 className="text-sm font-medium">No conversations yet</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Conversations will appear here once contacts receive messages.
                </p>
              </div>
            ) : (
              filtered.map((c) => (
                <ContactRow
                  key={c.contact_id}
                  contact={c}
                  active={selectedContact === c.contact_id}
                  onSelect={() => setSelectedContact(c.contact_id)}
                />
              ))
            )}
          </div>
        </div>

        {/* CENTER: thread + compose.
            Mobile (Unit M): full-width single pane shown once a contact is
            selected (hidden while the list is showing), with a back affordance +
            a context toggle. Desktop: the flex-1 center pane between the rails. */}
        {selectedContact ? (
          <div
            data-testid="inbox-thread-pane"
            className="flex-1 flex flex-col min-w-0"
          >
            {/* Mobile-only thread header: back to the list + open the context drawer. */}
            <div className="flex items-center justify-between gap-2 border-b px-3 py-2 md:hidden">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelectedContact(null)}
                data-testid="inbox-mobile-back"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Inbox
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowContext((v) => !v)}
                data-testid="inbox-context-toggle"
              >
                <Info className="h-4 w-4 mr-1" /> Details
              </Button>
            </div>
            <ContactThreads key={`threads-${selectedContact}`} contactId={selectedContact} />
          </div>
        ) : (
          <div className="hidden md:flex flex-1 flex-col items-center justify-center text-muted-foreground">
            <MessageSquare className="h-12 w-12 mb-3" />
            <p className="text-sm">Select a contact to view their conversations</p>
          </div>
        )}

        {/* RIGHT: contact-context panel.
            Desktop: inline aside (lg:flex). Mobile: a toggled drawer (showContext),
            full-width overlay-style pane so it is usable on a phone. */}
        {selectedContact && (
          <div
            data-testid="inbox-context-pane"
            className={cn(
              'min-w-0',
              showContext ? 'flex flex-1 md:flex-none' : 'hidden',
              'lg:flex'
            )}
          >
            <ContactContextPanel key={`panel-${selectedContact}`} contactId={selectedContact} />
          </div>
        )}
      </div>
    </div>
  );
}
